import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useEffect, useState } from 'react';

export interface NewBusinessFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Default name shown in the input (e.g. "Business form 1"). */
  defaultFormName: string;
  onCreate: (formName: string) => void | Promise<void>;
  loading?: boolean;
}

export function NewBusinessFormDialog({
  open,
  onOpenChange,
  defaultFormName,
  onCreate,
  loading = false,
}: NewBusinessFormDialogProps) {
  const [formName, setFormName] = useState(defaultFormName);

  useEffect(() => {
    if (open) {
      setFormName(defaultFormName);
    }
  }, [open, defaultFormName]);

  const handleSubmit = async () => {
    const name = formName.trim() || defaultFormName;
    await onCreate(name);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New business form</DialogTitle>
          <DialogDescription>
            Give this business form a name (e.g. Schedule C, 1065). You can change it later when editing the form.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label htmlFor="new-business-form-name">Form name</Label>
          <Input
            id="new-business-form-name"
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. Schedule C, 1065, 1120-S"
            disabled={loading}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
